import React, { useState } from 'react';
import { Modal, Input, Button, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { COLORS } from '../../../constants/colors';

const { TextArea } = Input;

const NeedsAttentionModal = ({ 
  visible, 
  onCancel, 
  onSave, 
  patient, 
  loading = false 
}) => {
  const [notes, setNotes] = useState('');
  const [localLoading, setLocalLoading] = useState(false);

  const handleSave = async () => {
    if (!notes.trim()) {
      message.error('Please enter a reason for why this patient needs attention');
      return;
    }

    setLocalLoading(true);
    try {
      await onSave(notes.trim());
      setNotes(''); // Clear notes after successful save
    } catch (error) {
      console.error('Error saving notes:', error);
      message.error('Failed to save notes. Please try again.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleCancel = () => {
    setNotes(''); // Clear notes when canceling
    onCancel();
  };

  return (
    <>
      <style>
        {`
          .needs-attention-modal .ant-modal-wrap {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .needs-attention-modal .ant-modal {
            position: relative !important;
            left: auto !important;
            top: auto !important;
            transform: none !important;
          }
          
          .needs-attention-modal .ant-modal-header {
            background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
            border-radius: 8px 8px 0 0;
            border-bottom: none;
            padding: 20px 24px;
          }
          
          .needs-attention-modal .ant-modal-title {
            color: white;
            font-size: 18px;
            font-weight: 600;
          }
          
          .needs-attention-modal .ant-modal-close {
            color: white;
            top: 16px;
            right: 16px;
          }
          
          .needs-attention-modal .ant-modal-close:hover {
            color: rgba(255, 255, 255, 0.8);
          }
          
          .needs-attention-modal .ant-modal-close .ant-modal-close-x {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            font-size: 16px;
          }
          
          .needs-attention-modal .ant-modal-content {
            border-radius: 8px;
            overflow: hidden;
            max-height: 90vh;
            overflow-y: auto;
          }
        `}
      </style>
      <Modal
        title="Mark Patient as Needs Attention"
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button 
            key="cancel" 
            onClick={handleCancel}
            disabled={localLoading || loading}
            size="large"
            style={{ marginRight: '12px' }}
          >
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={localLoading || loading}
            disabled={!notes.trim()}
            size="large"
          >
            Save & Mark for Attention
          </Button>
        ]}
        width={650}
        destroyOnClose
        className="needs-attention-modal"
        bodyStyle={{
          padding: '32px'
        }}
      >
      <div style={{ marginBottom: '32px' }}>
        <p style={{ 
          marginBottom: '24px', 
          color: COLORS.textDark,
          fontSize: '16px',
          lineHeight: '1.6'
        }}>
          <strong>Patient:</strong> {patient?.firstName} {patient?.lastName}
        </p>
        <p style={{ 
          marginBottom: '0', 
          color: COLORS.textMedium,
          fontSize: '15px',
          lineHeight: '1.6'
        }}>
          Please provide a detailed reason why this patient requires manual attention. 
          This information will help staff understand what needs to be addressed.
        </p>
      </div>

      <div style={{ marginBottom: '32px' }}>
        <label 
          style={{ 
            display: 'block', 
            marginBottom: '12px', 
            fontWeight: '600',
            color: COLORS.textDark,
            fontSize: '16px'
          }}
        >
          Reason for Attention <span style={{ color: COLORS.error }}>*</span>
        </label>
        <TextArea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter detailed notes about why this patient needs attention (e.g., medical concerns, incomplete information, special requirements, etc.)"
          rows={6}
          maxLength={1000}
          showCount
          disabled={localLoading || loading}
          style={{
            resize: 'vertical',
            minHeight: '140px',
            fontSize: '15px',
            lineHeight: '1.6',
            padding: '12px'
          }}
        />
      </div>

      <div style={{ 
        marginTop: '0', 
        padding: '20px', 
        backgroundColor: '#fff7e6', 
        borderRadius: '8px',
        border: 'none'
      }}>
        <p style={{ 
          margin: 0, 
          fontSize: '15px', 
          color: '#d46b08',
          fontWeight: '500',
          lineHeight: '1.5'
        }}>
          ⚠️ Note: This patient will be moved to the &quot;Needs Attention&quot; tab and removed from the active queue until the issue is resolved.
        </p>
      </div>
    </Modal>
    </>
  );
};

export default NeedsAttentionModal;
